import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { isApiError } from '@/api/errors';
import { previewTransfer, transferOrg } from '@/api/orgs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { QueryError } from '@/ui/QueryError';

const MAX_REASON = 1000;

export function TransferDialog({ orgId, onDone }: { orgId: string; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [target, setTarget] = useState('');
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);

  const preview = useQuery({
    queryKey: ['orgs', orgId, 'transfer-preview'],
    queryFn: () => previewTransfer(orgId),
    staleTime: 0,
  });

  const transfer = useMutation({
    mutationFn: () => transferOrg(orgId, target, reason.trim()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['orgs'] });
      void queryClient.invalidateQueries({ queryKey: ['direct-orgs'] });
      onDone();
    },
  });

  const reasonError = touched
    ? reason.trim() === ''
      ? 'A reason is required.'
      : reason.length > MAX_REASON
        ? 'The reason is too long.'
        : undefined
    : undefined;
  const valid = target !== '' && reason.trim() !== '' && reason.length <= MAX_REASON;

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Transfer organization</DialogTitle>
      </DialogHeader>

      {preview.isError ? (
        <QueryError error={preview.error} onRetry={() => void preview.refetch()} />
      ) : preview.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <form
          id="transfer-org"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            setTouched(true);
            if (valid) transfer.mutate();
          }}
        >
          <FieldGroup>
            <p className="text-sm">
              Currently with <strong>{preview.data.partner.name}</strong>.
            </p>
            {preview.data.plan_will_be_dropped && (
              <Alert variant="destructive">
                <AlertDescription>
                  The plan “{preview.data.plan?.name}” belongs to the current partner and cannot
                  follow the organization. After the move it has no plan, which means no limits,
                  until one is assigned.
                </AlertDescription>
              </Alert>
            )}
            {transfer.isError && (
              <Alert variant="destructive">
                <AlertDescription>
                  {isApiError(transfer.error) ? transfer.error.message : 'The transfer failed.'}
                </AlertDescription>
              </Alert>
            )}
            <Field>
              <FieldLabel htmlFor="transfer-target">New partner</FieldLabel>
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger id="transfer-target">
                  <SelectValue placeholder="Choose a partner" />
                </SelectTrigger>
                <SelectContent>
                  {preview.data.targets.map((partner) => (
                    <SelectItem key={partner.id} value={partner.id}>
                      {partner.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field data-invalid={Boolean(reasonError)}>
              <FieldLabel htmlFor="transfer-reason">Reason</FieldLabel>
              <Textarea
                id="transfer-reason"
                value={reason}
                aria-invalid={Boolean(reasonError)}
                onChange={(event) => setReason(event.target.value)}
                onBlur={() => setTouched(true)}
              />
              <FieldError>{reasonError}</FieldError>
            </Field>
          </FieldGroup>
        </form>
      )}

      <DialogFooter>
        <Button
          type="submit"
          form="transfer-org"
          disabled={!preview.data || transfer.isPending || (touched && !valid)}
        >
          {transfer.isPending ? 'Transferring…' : 'Transfer'}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
